"use client";

import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
  APPOINTMENT_WINDOW_DAYS,
  addDays,
  formatAppointmentTime,
  getIndiaNow,
} from "@/lib/appointmentSlots";

type PublicSlot = {
  start_time: string;
  end_time: string;
  display: string;
  available: boolean;
};

type PublicAppointment = {
  booking_id: string;
  client_name: string;
  client_phone: string;
  client_email: string | null;
  appointment_date: string;
  start_time: string;
  end_time: string;
  service_type: string;
  status: "confirmed";
  notes?: string;
};

const APPOINTMENT_API_BASE = "https://dashboard.quickint.in/api/public/appointments";

const emptyForm = {
  name: "",
  company: "",
  email: "",
  whatsapp: "",
  serviceType: "Project Consultation",
  details: "",
  location: "",
};

function formatDate(date: string, part: "weekday" | "day" | "month") {
  const value = new Date(`${date}T12:00:00+05:30`);
  if (part === "day") return new Intl.DateTimeFormat("en-IN", { day: "2-digit" }).format(value);
  if (part === "month") return new Intl.DateTimeFormat("en-IN", { month: "short" }).format(value);
  return new Intl.DateTimeFormat("en-IN", { weekday: "short" }).format(value);
}

function createDates() {
  const firstDate = addDays(getIndiaNow().date, 1);
  return Array.from({ length: APPOINTMENT_WINDOW_DAYS }, (_, index) =>
    addDays(firstDate, index)
  );
}

async function readApiError(response: Response) {
  const data = await response.json().catch(() => null);
  return typeof data?.error === "string" ? data.error : "Something went wrong. Please try again.";
}

export default function AppointmentForm() {
  const dates = useMemo(createDates, []);
  const [selectedDate, setSelectedDate] = useState(() => dates[0] ?? "");
  const [selectedTime, setSelectedTime] = useState("");
  const [slots, setSlots] = useState<PublicSlot[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [appointment, setAppointment] = useState<PublicAppointment | null>(null);

  const loadSlots = useCallback(async (date: string) => {
    if (!date) return;
    setLoadingSlots(true);
    setError("");
    try {
      const response = await fetch(
        `${APPOINTMENT_API_BASE}/slots?date=${encodeURIComponent(date)}`,
        { cache: "no-store" }
      );
      if (!response.ok) throw new Error(await readApiError(response));
      const data = (await response.json()) as { ok?: boolean; slots: PublicSlot[]; error?: string };
      if (data.ok === false) throw new Error(data.error || "Could not load available slots.");
      setSlots(data.slots.filter((slot) => slot.available));
      setSelectedTime((current) =>
        data.slots.some((slot) => slot.available && slot.start_time === current)
          ? current
          : ""
      );
    } catch (requestError) {
      setSlots([]);
      setError(requestError instanceof Error ? requestError.message : "Could not load available slots.");
    } finally {
      setLoadingSlots(false);
    }
  }, []);

  useEffect(() => {
    void loadSlots(selectedDate);
  }, [loadSlots, selectedDate]);

  const updateField = (field: keyof typeof emptyForm, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedDate || !selectedTime) {
      setError("Please choose an available date and time.");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      const response = await fetch(`${APPOINTMENT_API_BASE}/book`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          client_name: form.name,
          client_phone: form.whatsapp,
          client_email: form.email,
          appointment_date: selectedDate,
          start_time: selectedTime,
          service_type: form.serviceType,
          notes: `Company: ${form.company}\nLocation: ${form.location}\n\n${form.details}`,
        }),
      });
      if (!response.ok) throw new Error(await readApiError(response));
      const booking = (await response.json()) as { ok: boolean; booking_id: string; error?: string };
      if (!booking.ok) throw new Error(booking.error || "Could not request the appointment.");

      const lookupResponse = await fetch(
        `${APPOINTMENT_API_BASE}/${encodeURIComponent(booking.booking_id)}`,
        { cache: "no-store" }
      );
      if (!lookupResponse.ok) throw new Error(await readApiError(lookupResponse));
      const verified = (await lookupResponse.json()) as { appointment: PublicAppointment };
      setAppointment(verified.appointment);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Could not request the appointment.");
      await loadSlots(selectedDate);
    } finally {
      setSubmitting(false);
    }
  };

  const startAnotherBooking = () => {
    setAppointment(null);
    setSelectedTime("");
    setForm(emptyForm);
    setError("");
    void loadSlots(selectedDate);
  };

  if (appointment) {
    return (
      <section className="appointment-success" aria-live="polite">
        <div className="appointment-success-mark">✓</div>
        <span className="appointment-status">{appointment.status}</span>
        <h2>Your appointment is confirmed.</h2>
        <p>The dashboard API created and verified this booking. Your confirmation notifications will be handled by the dashboard.</p>
        <div className="appointment-summary">
          <span>{appointment.booking_id}</span>
          <span>{appointment.appointment_date}</span>
          <span>{formatAppointmentTime(appointment.start_time)} – {formatAppointmentTime(appointment.end_time)}</span>
          <span>{appointment.service_type}</span>
        </div>
        {error && <p className="form-error" role="alert">{error}</p>}
        <div className="appointment-success-actions">
          <button className="submit-btn" type="button" onClick={startAnotherBooking}>
            Book another appointment <span className="arrow">→</span>
          </button>
        </div>
      </section>
    );
  }

  return (
    <form className="appointment-form" id="booking" onSubmit={handleSubmit}>
      <div className="appointment-panel-head">
        <span>/ Appointment request</span>
        <span className="appointment-live">Live calendar · IST</span>
      </div>

      {error && <p className="form-error" role="alert">{error}</p>}

      <fieldset className="appointment-step">
        <legend><span>01</span> Choose an available date</legend>
        <div className="appointment-dates">
          {dates.map((date) => (
            <button
              type="button"
              key={date}
              className={selectedDate === date ? "appointment-date selected" : "appointment-date"}
              onClick={() => {
                setSelectedDate(date);
                setSelectedTime("");
              }}
              aria-pressed={selectedDate === date}
            >
              <span>{formatDate(date, "weekday")}</span>
              <strong>{formatDate(date, "day")}</strong>
              <small>{formatDate(date, "month")}</small>
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className="appointment-step" disabled={!selectedDate || loadingSlots}>
        <legend><span>02</span> Choose a time</legend>
        {loadingSlots ? (
          <div className="appointment-loading">Checking available slots…</div>
        ) : slots.length ? (
          <div className="appointment-times">
            {slots.map((slot) => (
              <button
                type="button"
                key={slot.start_time}
                className={selectedTime === slot.start_time ? "appointment-time selected" : "appointment-time"}
                onClick={() => setSelectedTime(slot.start_time)}
                aria-pressed={selectedTime === slot.start_time}
                title={slot.display}
              >
                {formatAppointmentTime(slot.start_time)}
              </button>
            ))}
          </div>
        ) : (
          <div className="appointment-empty">No available slots for this date.</div>
        )}
      </fieldset>

      <fieldset className="appointment-step appointment-details">
        <legend><span>03</span> Tell us who&apos;s joining</legend>
        <div className="input-row">
          <div className="field">
            <label className="field-label" htmlFor="appointment-name">Name <span className="req">*</span></label>
            <input id="appointment-name" className="input" name="name" required autoComplete="name" placeholder="Your full name" value={form.name} onChange={(event) => updateField("name", event.target.value)} />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="appointment-company">Company name <span className="req">*</span></label>
            <input id="appointment-company" className="input" name="company" required autoComplete="organization" placeholder="Company or organisation" value={form.company} onChange={(event) => updateField("company", event.target.value)} />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="appointment-email">Email <span className="req">*</span></label>
            <input id="appointment-email" className="input" name="email" required type="email" autoComplete="email" placeholder="you@company.com" value={form.email} onChange={(event) => updateField("email", event.target.value)} />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="appointment-whatsapp">WhatsApp phone number <span className="req">*</span></label>
            <input id="appointment-whatsapp" className="input" name="whatsapp" required type="tel" autoComplete="tel" placeholder="+91 98765 43210" value={form.whatsapp} onChange={(event) => updateField("whatsapp", event.target.value)} />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="appointment-service">Service type <span className="req">*</span></label>
            <select id="appointment-service" className="input" name="serviceType" required value={form.serviceType} onChange={(event) => updateField("serviceType", event.target.value)}>
              <option>Project Consultation</option>
              <option>AI &amp; Automation</option>
              <option>Software Development</option>
              <option>Web Development</option>
              <option>Mobile Development</option>
              <option>IT Consulting</option>
              <option>Cloud &amp; Managed Services</option>
            </select>
          </div>
          <div className="field">
            <label className="field-label" htmlFor="appointment-location">Location <span className="req">*</span></label>
            <input id="appointment-location" className="input" name="location" required autoComplete="address-level2" placeholder="City or office address" value={form.location} onChange={(event) => updateField("location", event.target.value)} />
          </div>
          <div className="field appointment-field-wide">
            <label className="field-label" htmlFor="appointment-details">What should we prepare? <span className="req">*</span></label>
            <textarea id="appointment-details" className="input" name="details" required placeholder="Share the problem, current workflow, or questions you want us to review before the meeting." value={form.details} onChange={(event) => updateField("details", event.target.value)} />
          </div>
        </div>
      </fieldset>

      <div className="appointment-submit-row">
        <p>Your appointment is confirmed through the Quickint dashboard.</p>
        <button className="submit-btn" type="submit" disabled={submitting || loadingSlots || !selectedTime}>
          {submitting ? "Requesting…" : "Request appointment"} <span className="arrow">→</span>
        </button>
      </div>
    </form>
  );
}